import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../../services/api';
import { playNotificationSound } from '../../services/notificationSound';

const POLL_INTERVAL = 10000; // 10 seconds

export default function OnlineMembersPanel({ onSelectMember, currentUserRole, activeChatUserId }) {
  const [onlineMembers, setOnlineMembers] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  const [search, setSearch] = useState('');
  const prevUnreadMapRef = useRef({});
  const initialLoadRef = useRef(true);

  const fetchOnline = useCallback(async () => {
    try {
      const res = await api.get('/accounts/online-members/');
      const members = res.data.members || [];
      setOnlineMembers(members);
      setCount(res.data.count || 0);

      // Detect new unread messages and play notification sound
      if (!initialLoadRef.current) {
        const prevMap = prevUnreadMapRef.current;
        let hasNewUnread = false;
        for (const member of members) {
          const prevCount = prevMap[member.id] || 0;
          const currCount = member.unread_count || 0;
          // Only notify if unread increased AND we're not already chatting with this user
          if (currCount > prevCount && member.id !== activeChatUserId) {
            hasNewUnread = true;
            break;
          }
        }
        if (hasNewUnread) {
          playNotificationSound();
        }
      }
      initialLoadRef.current = false;

      // Store current unread map for next comparison
      const newMap = {};
      for (const member of members) {
        newMap[member.id] = member.unread_count || 0;
      }
      prevUnreadMapRef.current = newMap;
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [activeChatUserId]);

  useEffect(() => {
    fetchOnline();
    const interval = setInterval(fetchOnline, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchOnline]);

  const filtered = search.trim()
    ? onlineMembers.filter(
        (m) =>
          m.name.toLowerCase().includes(search.toLowerCase()) ||
          (m.membership_number || '').toLowerCase().includes(search.toLowerCase())
      )
    : onlineMembers;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#062E10] to-[#0B5D1E] text-white hover:from-[#0B5D1E] hover:to-[#0e7526] transition-all"
      >
        <div className="flex items-center gap-2.5">
          <div className="relative">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-green-400 rounded-full border-2 border-[#062E10] animate-pulse" />
          </div>
          <span className="text-sm font-semibold">Online Members</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="bg-white/20 text-xs font-bold px-2 py-0.5 rounded-full min-w-[24px] text-center">
            {count}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* Body */}
      {!collapsed && (
        <div>
          {/* Search */}
          <div className="px-3 pt-3 pb-1">
            <div className="relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search online members..."
                className="w-full pl-8 pr-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-xs focus:ring-2 focus:ring-[#0B5D1E]/30 focus:border-[#0B5D1E] outline-none"
              />
            </div>
            {!loading && count > 0 && (
              <p className="text-[10px] text-gray-400 mt-1.5 px-1">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
                  {count} member{count !== 1 ? 's' : ''} online now
                </span>
              </p>
            )}
          </div>

          {/* Members List */}
          <div className="max-h-[350px] overflow-y-auto px-2 pb-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-5 h-5 border-2 border-[#0B5D1E]/20 border-t-[#0B5D1E] rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-6 px-4">
                <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-2">
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <p className="text-xs text-gray-500">
                  {search ? 'No matching online members' : 'No members online'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {filtered.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => onSelectMember && onSelectMember(member)}
                    className="w-full flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-[#0B5D1E]/5 transition-all group text-left"
                  >
                    {/* Avatar with online dot */}
                    <div className="relative flex-shrink-0">
                      {member.photo ? (
                        <img
                          src={member.photo}
                          alt={member.name}
                          className="w-11 h-11 rounded-full object-cover border-2 border-gray-200 group-hover:border-[#0B5D1E]/30"
                        />
                      ) : (
                        <div className="w-11 h-11 rounded-full bg-[#0B5D1E]/10 text-[#0B5D1E] flex items-center justify-center text-base font-bold group-hover:bg-[#0B5D1E]/20">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                      {/* Green online indicator */}
                      <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 rounded-full border-2 border-white shadow-sm" />
                    </div>

                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold text-gray-900 truncate group-hover:text-[#0B5D1E] transition">
                        {member.name}
                      </p>
                      {member.membership_number && (
                        <p className="text-xs text-gray-500 truncate">
                          NIS/ <span className="font-medium">{member.membership_number}</span>
                        </p>
                      )}
                      {member.membership_category && (
                        <p className="text-[11px] text-gray-400 truncate">
                          {member.membership_category}
                        </p>
                      )}
                    </div>

                    {/* Role badge */}
                    {member.role === 'admin' && (
                      <span className="text-[9px] px-1.5 py-0.5 rounded bg-[#C8A951]/20 text-[#8B6914] font-semibold flex-shrink-0">
                        Admin
                      </span>
                    )}

                    {/* Chat icon with unread badge */}
                    {onSelectMember && (
                      <div className="relative flex-shrink-0">
                        <svg className="w-5 h-5 text-gray-400 group-hover:text-[#0B5D1E] transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                        {member.unread_count > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 w-4 h-4 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center shadow-sm">
                            {member.unread_count > 9 ? '9+' : member.unread_count}
                          </span>
                        )}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>


        </div>
      )}
    </div>
  );
}
